import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import service from '../api/ApiRoutes';

const initialState = {
  maintenances: [],
  maintenance: null,
  totalMaintenances: 0,
  totalPages: 1,
  currentPage: 1,
  status: 'idle',
  error: null,
  successMessage: '',
  searchQuery: '',
};

// Create a new maintenance record
export const createMaintenance = createAsyncThunk(
  'maintenance/createMaintenance',
  async (maintenanceData, thunkAPI) => {
    try {
      const response = await service.createMaintenance(maintenanceData);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Fetch maintenance records with search and pagination
export const fetchMaintenances = createAsyncThunk(
  'maintenance/fetchMaintenances',
  async ({ page, searchQuery }, thunkAPI) => {
    try {
      const response = await service.getMaintenances({ page, searchTerm: searchQuery });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

// Fetch all maintenance records (without pagination)
export const fetchAllMaintenances = createAsyncThunk(
  'maintenance/fetchAllMaintenances',
  async (config, thunkAPI) => {
    try {
      const response = await service.getAllMaintenances(config);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Fetch a single maintenance record by ID
export const fetchMaintenanceById = createAsyncThunk(
  'maintenance/fetchMaintenanceById',
  async (maintenanceId, thunkAPI) => {
    try {
      const response = await service.getMaintenanceById(maintenanceId);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Update a maintenance record
export const updateMaintenance = createAsyncThunk(
  'maintenance/updateMaintenance',
  async ({ id, maintenanceData, config }, thunkAPI) => {
    try {
      const response = await service.updateMaintenance(id, maintenanceData, config);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Delete a maintenance record
export const deleteMaintenance = createAsyncThunk(
  'maintenance/deleteMaintenance',
  async (maintenanceId, thunkAPI) => {
    try {
      await service.deleteMaintenance(maintenanceId);
      return maintenanceId;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Slice
const maintenanceSlice = createSlice({
  name: 'maintenance',
  initialState,
  reducers: {
    resetState(state) {
      state.error = null;
      state.successMessage = '';
      state.status = 'idle';
    },
    setSearchQuery(state, action) {
      state.searchQuery = action.payload;
    },
    setCurrentPage(state, action) {
      state.currentPage = action.payload;
    },
    removeMaintenance(state, action) {
      state.maintenances = state.maintenances.filter(m => m._id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createMaintenance.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createMaintenance.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.maintenances.push(action.payload);
        state.successMessage = 'Maintenance record created successfully!';
      })
      .addCase(createMaintenance.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      .addCase(fetchMaintenances.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchMaintenances.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.maintenances = action.payload.maintenance;
        state.totalPages = action.payload.totalPages;
        state.totalMaintenances = action.payload.totalMaintenance;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchMaintenances.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      .addCase(fetchAllMaintenances.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAllMaintenances.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.maintenances = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.maintenances || [];
      })
      .addCase(fetchAllMaintenances.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.message || 'Failed to fetch maintenances';
      })

      .addCase(fetchMaintenanceById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchMaintenanceById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.maintenance = action.payload;
      })
      .addCase(fetchMaintenanceById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      .addCase(updateMaintenance.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateMaintenance.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const index = state.maintenances.findIndex(m => m._id === action.payload._id);
        if (index !== -1) {
          state.maintenances[index] = action.payload;
        }
        state.successMessage = 'Maintenance record updated successfully!';
      })
      .addCase(updateMaintenance.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      .addCase(deleteMaintenance.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteMaintenance.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.maintenances = state.maintenances.filter(m => m._id !== action.payload);
        state.successMessage = 'Maintenance record deleted successfully!';
      })
      .addCase(deleteMaintenance.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export const { resetState, setSearchQuery, setCurrentPage, removeMaintenance } = maintenanceSlice.actions;
export default maintenanceSlice.reducer;
